import { useFormattedSocialProof } from "../../../../hooks/useFormattedSocialProof";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export const TopGalleryHeroImgStatisticsUpMd = () => {
  const { t, i18n } = useTranslation(["sellWithUs"]);
  const isRTL = i18n.dir() === "rtl";
  const { activeInvestors, successfulProjects, formattedSalesAmount } =
    useFormattedSocialProof();
  const downMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollToStart = () => {
      if (sliderRef.current) {
        const scrollPosition = isRTL
          ? sliderRef.current.scrollWidth - sliderRef.current.clientWidth
          : 0;
        sliderRef.current.scrollTo({
          left: scrollPosition,
          behavior: "auto",
        });
      }
    };
    requestAnimationFrame(scrollToStart);
  }, [isRTL]);

  return (
    <Box
      ref={sliderRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: "15px", md: "20px" },
        height: "80%",
        alignItems: "flex-start",
        justifyContent: downMd
          ? isRTL
            ? "flex-end"
            : "flex-start"
          : isRTL
          ? "flex-end"
          : "flex-end",
        color: "white",
        fontFamily: isRTL ? "Noto Sans Hebrew" : "DM Sans",
        mb: "0",
        scrollSnapType: downMd ? "x mandatory" : "none",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        scrollBehavior: "smooth",
        direction: isRTL ? "rtl" : "ltr",
        padding: { xs: "20px", md: "30px" },
      }}
    >
      {/* Active Investors - First Stat */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: isRTL ? "flex-end" : "flex-start",
          alignItems: "center",
          gap: { xs: "8px", sm: "10px", md: "12px" },
          scrollSnapAlign: downMd ? (isRTL ? "end" : "start") : "none",
          px: downMd ? 1 : 0,
        }}
      >
        <Box
          sx={{
            bgcolor: "#FFFFFF47",
            backdropFilter: "blur(15px)",
            borderRadius: "50%",
            width: { xs: "60px", sm: "70px", md: "100px" },
            height: { xs: "60px", sm: "70px", md: "100px" },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
            direction: "ltr",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "18px", md: "24px" },
              fontWeight: "600",
              fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
              textAlign: "left",
            }}
          >
            {activeInvestors}+
          </Typography>
        </Box>
        <Box sx={{ textAlign: "left" }}>
          <Typography
            sx={{
              fontSize: { xs: "14px", sm: "16px", md: "24px" },
              lineHeight: "1.2",
              fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
              fontWeight: isRTL ? "700" : "600",
            }}
          >
            {t("active")}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "14px", sm: "16px", md: "24px" },
              lineHeight: "1.2",
              fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
              fontWeight: isRTL ? "700" : "600",
            }}
          >
            {t("investors")}
          </Typography>
        </Box>
      </Box>

      {/* Successful Projects - Second Stat */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: isRTL ? "flex-end" : "flex-start",
          gap: { xs: "8px", sm: "10px", md: "12px" },

          scrollSnapAlign: downMd ? (isRTL ? "end" : "start") : "none",
          px: downMd ? 1 : 0,
        }}
      >
        <Box
          sx={{
            bgcolor: "#FFFFFF47",
            backdropFilter: "blur(15px)",
            borderRadius: "50%",
            width: { xs: "60px", sm: "70px", md: "100px" },
            height: { xs: "60px", sm: "70px", md: "100px" },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
            direction: "ltr",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "18px", md: "24px" },
              fontWeight: "700",
              fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
            }}
          >
            {successfulProjects}+
          </Typography>
        </Box>
        <Box sx={{ textAlign: "left", width: "auto" }}>
          <Typography
            sx={{
              fontSize: { xs: "14px", sm: "16px", md: "24px" },
              lineHeight: "1.2",
              fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
              fontWeight: isRTL ? "700" : "600",
            }}
          >
            {t("successful")}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "14px", sm: "16px", md: "24px" },
              lineHeight: "1.2",
              fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
              fontWeight: isRTL ? "700" : "600",
            }}
          >
            {t("projects")}
          </Typography>
        </Box>
      </Box>

      {/* In Real Estate Sales - Third Stat */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: isRTL ? "flex-end" : "flex-start",
          gap: { xs: "8px", sm: "10px", md: "12px" },
          scrollSnapAlign: downMd ? (isRTL ? "end" : "start") : "none",
          px: downMd ? 1 : 0,
        }}
      >
        <Box
          sx={{
            bgcolor: "#FFFFFF47",
            backdropFilter: "blur(15px)",
            borderRadius: "50%",
            width: { xs: "60px", sm: "70px", md: "100px" },
            height: { xs: "60px", sm: "70px", md: "100px" },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "18px", md: "24px" },
              fontWeight: "600",
              direction: "ltr",
              fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
            }}
          >
            {isRTL ? "₪" : "$"} {formattedSalesAmount}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "left", width: "auto" }}>
          <Typography
            sx={{
              fontSize: { xs: "14px", sm: "16px", md: "24px" },
              lineHeight: "1.2",
              fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
              fontWeight: isRTL ? "700" : "600",
            }}
          >
            {t("in_re")}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "14px", sm: "16px", md: "24px" },
              lineHeight: "1.2",
              fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
              fontWeight: isRTL ? "700" : "600",
            }}
          >
            {t("sales")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
